// SettingsPathPane.jsx

import React, { useEffect, useMemo, useState } from 'react';

// Context
import { useWorkspace } from 'context/WorkspaceContext';

// Device Detection
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { updateWorkspace } from 'services/workspaces/WorkspaceWriteService';

// Utilities
import {
    buildPublicPageRoute,
    getWorkspacePathValue,
    normalizePublicPath,
} from 'utilities/PublicPaths';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

/**
 * SettingsPathPane
 *
 * Renders an editable field for the workspace public path.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} workspace - Active workspace model.
 * @param {boolean} canEditWorkspace - Whether the current user can edit workspace settings.
 *
 * @returns {ReactNode} The rendered path settings pane.
 */
const SettingsPathPane = React.memo(({
    theme,
    workspace,
    canEditWorkspace,
}) => {

    // ----- CONTEXT ----- //

    const { setActiveWorkspace } = useWorkspace();

    // ----- DEVICE DETECTION ----- //

    const isMobile = useIsMobile();

    // ----- STATE ----- //

    const [pathValue, setPathValue] = useState(getWorkspacePathValue(workspace));
    const [isSaving, setIsSaving] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [statusMessage, setStatusMessage] = useState('');

    // ----- EFFECTS ----- //

    useEffect(() => {
        setPathValue(getWorkspacePathValue(workspace));
    }, [workspace]);

    // ----- MEMOS ----- //

    const normalizedPathValue = useMemo(() => normalizePublicPath(pathValue), [pathValue]);
    const persistedPathValue = getWorkspacePathValue(workspace);
    const hasChanges = normalizedPathValue !== persistedPathValue;
    const publicPathPreview = buildPublicPageRoute({
        workspacePath: normalizedPathValue,
        fallbackPagePath: '...',
        pagePath: '',
    });

    // ----- HANDLERS ----- //

    /**
     * handleChange
     *
     * Updates the controlled input value and clears stale messages.
     *
     * @param {object} event - Native input change event.
     */
    const handleChange = (event) => {
        setPathValue(event.target.value);
        setErrorMessage('');
        setStatusMessage('');
    };

    /**
     * handleSave
     *
     * Persists the new workspace path via the write service.
     *
     * @returns {Promise<void>} Resolves when the save flow completes.
     */
    const handleSave = async () => {
        if (isSaving || !workspace?.key) return;

        if (!normalizedPathValue) {
            setErrorMessage('Workspace path cannot be empty.');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Saving...');
        setIsSaving(true);

        try {
            await updateWorkspace(workspace.key, { path: normalizedPathValue });
            setActiveWorkspace((previous) => {
                if (!previous || previous.key !== workspace.key) return previous;
                return { ...previous, path: normalizedPathValue };
            });
            setStatusMessage('Workspace path updated.');
            setPathValue(normalizedPathValue);
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update workspace path.');
            setStatusMessage('');
        } finally {
            setIsSaving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div
                className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                }}
            >
                <div className="settings-modal-pane-header-left">
                    <div
                        className="settings-modal-pane-title"
                        style={{
                            color: theme.foreground,
                        }}
                    >
                        Path
                    </div>
                    <div
                        className="settings-modal-pane-subtitle"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Workspace public namespace.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div
                    className="settings-modal-name-card"
                    style={{
                        borderColor: theme.backgroundHover,
                    }}
                >
                    <TextInput
                        value={pathValue}
                        onChange={handleChange}
                        onEnterPress={hasChanges ? handleSave : undefined}
                        disabled={!canEditWorkspace || isSaving}
                        maxLength={100}
                        width="100%"
                    />

                    <Button
                        iconKey="save"
                        label="Save"
                        onClick={handleSave}
                        enabled={canEditWorkspace && hasChanges && !isSaving}
                    />
                </div>

                <div
                    className="settings-modal-note"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Public path preview: {publicPathPreview}
                </div>

                <div
                    className="settings-modal-note"
                    style={{
                        color: theme.foregroundMuted,
                    }}
                >
                    Changing the workspace path changes the canonical public URL for all published pages in this workspace.
                </div>

                {!canEditWorkspace && (
                    <div
                        className="settings-modal-note"
                        style={{
                            color: theme.foregroundMuted,
                        }}
                    >
                        Only admins and owners can update the workspace path.
                    </div>
                )}

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}

                {errorMessage && (
                    <div className="settings-modal-error">{errorMessage}</div>
                )}
            </div>
        </>
    );
});

// Dev Tools
SettingsPathPane.displayName = 'SettingsPathPane';

export default SettingsPathPane;